import { useEffect, useRef, useState } from 'react';
import Button from '../atoms/Button';

interface WalletConnectModalProps {
  open: boolean;
  onClose: () => void;
  onConnected?: (address: string) => void;
}

type WalletProvider = 'WalletConnect' | 'TronLink' | 'MetaMask';

const PROVIDERS: Array<{ id: WalletProvider; mark: string; sub: string; tone: string }> = [
  { id: 'WalletConnect', mark: 'WC', sub: '多链支持', tone: 'bg-jade-100 text-jade-700' },
  { id: 'TronLink', mark: 'TL', sub: 'TRC-20 直连', tone: 'bg-gold-100 text-gold-700' },
  { id: 'MetaMask', mark: 'MM', sub: 'ERC-20', tone: 'bg-vermilion-50 text-vermilion-700' },
];

export function WalletConnectModal({ open, onClose, onConnected }: WalletConnectModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [selected, setSelected] = useState<WalletProvider>('WalletConnect');
  const [status, setStatus] = useState<'idle' | 'connecting' | 'connected'>('idle');

  useEffect(() => {
    if (!open) {
      setStatus('idle');
      setSelected('WalletConnect');
      return;
    }
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && status !== 'connecting') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, status]);

  if (!open) return null;

  const connect = () => {
    setStatus('connecting');
    window.setTimeout(() => {
      setStatus('connected');
      onConnected?.('TQ9x...7m2p');
    }, 500);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="wallet-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/40 px-6 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && status !== 'connecting') onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-hairline bg-paper p-8 shadow-2xl animate-modalIn">
        <h2 id="wallet-title" className="text-center font-serif text-2xl font-medium text-ink">
          连接 USDT 钱包
        </h2>
        <p className="mt-2 text-center text-sm leading-relaxed text-ink-2">
          签名验证身份，不付款。每钱包 1 次 free sample。
        </p>

        <div className="mt-6 space-y-2">
          {PROVIDERS.map((provider) => (
            <button
              key={provider.id}
              type="button"
              className={`flex w-full items-center gap-3 rounded border px-4 py-3 text-left transition ${
                selected === provider.id
                  ? 'border-jade bg-jade-50'
                  : 'border-hairline bg-canvas hover:border-jade'
              }`}
              onClick={() => setSelected(provider.id)}
            >
              <span className={`grid h-8 w-8 place-items-center rounded font-mono text-xs font-semibold ${provider.tone}`}>
                {provider.mark}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-ink">{provider.id}</span>
                <span className="block text-xs text-ink-2">{provider.sub}</span>
              </span>
              <span className="font-mono text-xs text-jade-700">{selected === provider.id ? 'selected' : ''}</span>
            </button>
          ))}
        </div>

        <div className="mt-5 rounded border border-hairline bg-canvas p-3 text-xs leading-relaxed text-ink-2">
          没有钱包？打开 FAQ 查看 TronLink / MetaMask 安装说明。
        </div>

        {status === 'connected' && (
          <div className="mt-4 rounded border border-jade-200 bg-jade-50 p-3 text-sm text-jade-700">
            Connected · TQ9x...7m2p
          </div>
        )}

        <div className="mt-6 flex gap-3">
          <Button ref={closeRef} variant="secondary" block onClick={onClose} disabled={status === 'connecting'}>
            取消
          </Button>
          <Button variant="primary" block loading={status === 'connecting'} onClick={connect}>
            Sign with {selected}
          </Button>
        </div>
      </div>
    </div>
  );
}
